/**
 * Canlı destek tipleri — backend'deki LiveChatViewModels ile eşleşir.
 *
 * Enum'lar REST'te isim ("Agent"), SignalR'da sayı (1) olarak gelir: hub protokolünde
 * JsonStringEnumConverter kayıtlı değil. Okumadan önce normalize edilmeli.
 */

export type LiveChatStatus = "Bot" | "WaitingForAgent" | "Active" | "Resolved" | "Closed";

export type LiveChatTopic =
  | "Unspecified"
  | "OrderIssue"
  | "DeliveryIssue"
  | "MissingOrWrongItem"
  | "FoodQuality"
  | "PaymentOrRefund"
  | "PromoCode"
  | "AccountOrApp"
  | "Other";

export type LiveChatPriority = "Low" | "Normal" | "High" | "Urgent";
export type LiveChatSenderRole = "Customer" | "Agent" | "Bot" | "System" | "Driver";
export type LiveChatMessageType =
  | "Text" | "Image" | "System" | "OrderCard" | "QuickReply" | "RatingRequest";
export type LiveChatChannel = "CustomerApp" | "DriverApp" | "Web";
export type LiveChatAgentAvailability = "Offline" | "Online" | "Away" | "Busy";

export interface ILiveChatMessage {
  id: string;
  conversationId: string;
  senderId?: string | null;
  senderRole: LiveChatSenderRole;
  senderName?: string | null;
  messageType: LiveChatMessageType;
  content: string;
  /** Mutlak S3 adresi. */
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  attachmentSizeBytes?: number | null;
  metadataJson?: string | null;
  clientMessageId?: string | null;
  sentAt: string;
  readAt?: string | null;
  isRead: boolean;
}

export interface ILiveChatOrderContext {
  orderId: string;
  orderNo?: string | null;
  orderDate: string;
  orderPrice: number;
  statusName?: string | null;
  restaurantName?: string | null;
  estimatedDeliveryAt?: string | null;
  isCancelled: boolean;
}

export interface ILiveChatQueueStatus {
  conversationId: string;
  position: number;
  estimatedWaitMinutes: number;
  onlineAgentCount: number;
  agentsAvailable: boolean;
}

export interface ILiveChatQuickReply {
  id: string;
  title: string;
  content: string;
  topic?: LiveChatTopic | null;
  forRole: LiveChatSenderRole;
  languageCode: string;
  sortOrder: number;
  isActive: boolean;
  usageCount: number;
}

export interface ILiveChatConversation {
  id: string;
  conversationNo: string;
  customerId: string;
  customerName?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  orderId?: string | null;
  order?: ILiveChatOrderContext | null;
  topic: LiveChatTopic;
  status: LiveChatStatus;
  priority: LiveChatPriority;
  channel: LiveChatChannel;
  isBotHandled: boolean;
  unreadCount: number;
  createdAt: string;
  queuedAt?: string | null;
  assignedAt?: string | null;
  firstResponseAt?: string | null;
  lastMessageAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  rating?: number | null;
  ratingComment?: string | null;
  queue?: ILiveChatQueueStatus | null;
  suggestedReplies: ILiveChatQuickReply[];
  messages: ILiveChatMessage[];
}

export interface ILiveChatConversationSummary {
  id: string;
  conversationNo: string;
  customerId: string;
  customerName?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  orderId?: string | null;
  topic: LiveChatTopic;
  status: LiveChatStatus;
  priority: LiveChatPriority;
  channel: LiveChatChannel;
  lastMessagePreview?: string | null;
  createdAt: string;
  queuedAt?: string | null;
  lastMessageAt?: string | null;
  unreadCount: number;
  waitingMinutes?: number | null;
  rating?: number | null;
}

export interface ILiveChatAgentStatus {
  agentId: string;
  agentName?: string | null;
  availability: LiveChatAgentAvailability;
  maxConcurrentChats: number;
  activeChatCount: number;
  lastSeenAt: string;
}

export interface ILiveChatDashboard {
  waitingCount: number;
  activeCount: number;
  botHandledCount: number;
  resolvedLast24h: number;
  averageFirstResponseSeconds?: number | null;
  averageResolutionSeconds?: number | null;
  averageRating?: number | null;
  botDeflectionRate: number;
  longestWaitingMinutes: number;
  agents: ILiveChatAgentStatus[];
}

export interface ILiveChatMessagePage {
  conversationId: string;
  messages: ILiveChatMessage[];
  nextCursor?: string | null;
  hasMore: boolean;
}

/* ------------------------------------------------------------------ */
/* Wire normalizasyonu                                                 */
/* ------------------------------------------------------------------ */

const STATUS: Record<number, LiveChatStatus> = {
  0: "Bot", 1: "WaitingForAgent", 2: "Active", 3: "Resolved", 4: "Closed",
};

const TOPIC: Record<number, LiveChatTopic> = {
  0: "Unspecified", 1: "OrderIssue", 2: "DeliveryIssue", 3: "MissingOrWrongItem",
  4: "FoodQuality", 5: "PaymentOrRefund", 6: "PromoCode", 7: "AccountOrApp", 99: "Other",
};

const PRIORITY: Record<number, LiveChatPriority> = {
  0: "Low", 1: "Normal", 2: "High", 3: "Urgent",
};

const SENDER_ROLE: Record<number, LiveChatSenderRole> = {
  0: "Customer", 1: "Agent", 2: "Bot", 3: "System", 4: "Driver",
};

const MESSAGE_TYPE: Record<number, LiveChatMessageType> = {
  0: "Text", 1: "Image", 2: "System", 3: "OrderCard", 4: "QuickReply", 5: "RatingRequest",
};

const CHANNEL: Record<number, LiveChatChannel> = {
  0: "CustomerApp", 1: "DriverApp", 2: "Web",
};

const AVAILABILITY: Record<number, LiveChatAgentAvailability> = {
  0: "Offline", 1: "Online", 2: "Away", 3: "Busy",
};

const toName = <T extends string>(value: unknown, map: Record<number, T>, fallback: T): T => {
  if (typeof value === "string") return value as T;
  if (typeof value === "number") return map[value] ?? fallback;
  return fallback;
};

/** SignalR tarafında saat dilimi eki gelmeyebilir; yerel saat sanılmasın. */
const toUtcIso = (value: unknown): string => {
  if (typeof value !== "string" || value.length === 0) return new Date().toISOString();
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`;
};

const toUtcIsoOrNull = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? toUtcIso(value) : null;

export const normalizeMessage = (raw: ILiveChatMessage): ILiveChatMessage => ({
  ...raw,
  senderRole: toName(raw.senderRole, SENDER_ROLE, "System"),
  messageType: toName(raw.messageType, MESSAGE_TYPE, "Text"),
  sentAt: toUtcIso(raw.sentAt),
  readAt: toUtcIsoOrNull(raw.readAt),
});

export const normalizeSummary = (
  raw: ILiveChatConversationSummary
): ILiveChatConversationSummary => ({
  ...raw,
  status: toName(raw.status, STATUS, "Bot"),
  topic: toName(raw.topic, TOPIC, "Unspecified"),
  priority: toName(raw.priority, PRIORITY, "Normal"),
  channel: toName(raw.channel, CHANNEL, "Web"),
});

export const normalizeConversation = (raw: ILiveChatConversation): ILiveChatConversation => ({
  ...raw,
  status: toName(raw.status, STATUS, "Bot"),
  topic: toName(raw.topic, TOPIC, "Unspecified"),
  priority: toName(raw.priority, PRIORITY, "Normal"),
  channel: toName(raw.channel, CHANNEL, "Web"),
  suggestedReplies: (raw.suggestedReplies ?? []).map((r) => ({
    ...r,
    forRole: toName(r.forRole, SENDER_ROLE, "Agent"),
  })),
  messages: (raw.messages ?? []).map(normalizeMessage),
});

export const normalizeAgentStatus = (raw: ILiveChatAgentStatus): ILiveChatAgentStatus => ({
  ...raw,
  availability: toName(raw.availability, AVAILABILITY, "Offline"),
});

export const parseQuickReplyOptions = (metadataJson?: string | null): string[] => {
  if (!metadataJson) return [];
  try {
    const parsed = JSON.parse(metadataJson) as { options?: unknown };
    return Array.isArray(parsed.options)
      ? (parsed.options.filter((o) => typeof o === "string") as string[])
      : [];
  } catch {
    return [];
  }
};

export const parseOrderCard = (metadataJson?: string | null): ILiveChatOrderContext | null => {
  if (!metadataJson) return null;
  try {
    return JSON.parse(metadataJson) as ILiveChatOrderContext;
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* Görüntüleme yardımcıları                                            */
/* ------------------------------------------------------------------ */

export const TOPIC_LABELS: Record<LiveChatTopic, string> = {
  Unspecified: "Belirtilmemiş",
  OrderIssue: "Sipariş Sorunu",
  DeliveryIssue: "Teslimat Sorunu",
  MissingOrWrongItem: "Eksik / Yanlış Ürün",
  FoodQuality: "Yemek Kalitesi",
  PaymentOrRefund: "Ödeme / İade",
  PromoCode: "Promosyon Kodu",
  AccountOrApp: "Hesap / Uygulama",
  Other: "Diğer",
};

export const STATUS_LABELS: Record<LiveChatStatus, string> = {
  Bot: "Asistan",
  WaitingForAgent: "Kuyrukta",
  Active: "Görüşülüyor",
  Resolved: "Çözüldü",
  Closed: "Kapandı",
};

export const PRIORITY_LABELS: Record<LiveChatPriority, string> = {
  Low: "Düşük",
  Normal: "Normal",
  High: "Yüksek",
  Urgent: "Acil",
};

export const PRIORITY_COLORS: Record<LiveChatPriority, string> = {
  Low: "#9ca3af",
  Normal: "#6366f1",
  High: "#f59e0b",
  Urgent: "#dc2626",
};

export const AVAILABILITY_LABELS: Record<LiveChatAgentAvailability, string> = {
  Offline: "Çevrimdışı",
  Online: "Çevrimiçi",
  Away: "Uzakta",
  Busy: "Meşgul",
};
